import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, CaretUp } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { joinStrings } from "@/lib/joinString"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import type { DeBetterDocRecommendedCareProvider } from "@/modules/patients/detail/better-doc/types"

const ALWAYS_VISIBLE_CARE_PROVIDER_COUNT = 3

type DeBetterDocRecommendedCareProviderItemProps = {
  careProvider: DeBetterDocRecommendedCareProvider
}

const DeBetterDocRecommendedCareProviderItem = ({ careProvider }: DeBetterDocRecommendedCareProviderItemProps) => {
  const { position, name, workplaceName, street, postalCode, city } = careProvider
  const addressLine = joinStrings([street, joinStrings([postalCode, city], " ")], ", ")
  const careProviderTitle = name ?? workplaceName
  const careProviderDetails = joinStrings([name ? workplaceName : null, addressLine], ", ")

  return (
    <li
      data-testid={`de-better-doc-recommended-care-provider-${position}`}
      className="border-border bg-surface flex min-h-12 w-full items-center rounded-xl border px-2 py-1">
      <CareProviderIcon groupType="DOCTOR" />
      <div className="ml-2 grid min-w-0">
        {careProviderTitle ? (
          <span className="truncate text-sm" title={careProviderTitle}>
            {careProviderTitle}
          </span>
        ) : null}
        {careProviderDetails ? (
          <span className="text-on-surface-subdued truncate text-xs" title={careProviderDetails}>
            {careProviderDetails}
          </span>
        ) : null}
      </div>
    </li>
  )
}

type DeBetterDocRecommendedCareProvidersProps = {
  recommendedCareProviders: ReadonlyArray<DeBetterDocRecommendedCareProvider>
}

export const DeBetterDocRecommendedCareProviders = ({
  recommendedCareProviders,
}: DeBetterDocRecommendedCareProvidersProps) => {
  const { t } = useTranslation()
  const [areFurtherCareProvidersVisible, setAreFurtherCareProvidersVisible] = useState(false)
  const sortedCareProviders = [...recommendedCareProviders].sort((first, second) => first.position - second.position)
  const alwaysVisibleCareProviders = sortedCareProviders.slice(0, ALWAYS_VISIBLE_CARE_PROVIDER_COUNT)
  const furtherCareProviders = sortedCareProviders.slice(ALWAYS_VISIBLE_CARE_PROVIDER_COUNT)
  const hasFurtherCareProviders = furtherCareProviders.length > 0

  return (
    <Collapsible
      open={areFurtherCareProvidersVisible}
      onOpenChange={setAreFurtherCareProvidersVisible}
      data-testid="de-better-doc-recommended-care-providers">
      <div className="flex min-h-10 items-center justify-between gap-2">
        <span className="text-on-surface text-sm font-medium">
          {t("episodeOfCare.betterDoc.result.recommendedCareProviders", { count: sortedCareProviders.length })}
        </span>
        {hasFurtherCareProviders ? (
          <CollapsibleTrigger asChild>
            <Button
              variant="circle"
              className="h-10 w-10"
              data-testid="de-better-doc-recommended-care-providers-toggle">
              {areFurtherCareProvidersVisible ? (
                <CaretUp className="text-on-surface-subdued" />
              ) : (
                <CaretDown className="text-on-surface-subdued" />
              )}
            </Button>
          </CollapsibleTrigger>
        ) : null}
      </div>
      <ul className="flex flex-col gap-2 pt-2">
        {alwaysVisibleCareProviders.map((careProvider) => (
          <DeBetterDocRecommendedCareProviderItem key={careProvider.position} careProvider={careProvider} />
        ))}
      </ul>
      {hasFurtherCareProviders ? (
        <CollapsibleContent>
          <ul className="flex flex-col gap-2 pt-2">
            {furtherCareProviders.map((careProvider) => (
              <DeBetterDocRecommendedCareProviderItem key={careProvider.position} careProvider={careProvider} />
            ))}
          </ul>
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  )
}
